import {useCallback, useMemo} from 'react'

import {restApi} from 'src/services/rest'
import type {BuildId} from 'src/types'

export function useQueryDependenciesSearch() {
  const [fetchParametersByName, parametersByName] =
    restApi.endpoints.getBuildProperties.useLazyQuery({
      selectFromResult: ({data, isLoading, isFetching}) => ({
        resultingProperties: data?.resultingProperties,
        isLoading: isLoading || isFetching,
      }),
    })

  const [fetchParametersByValue, parametersByValue] =
    restApi.endpoints.getBuildProperties.useLazyQuery({
      selectFromResult: ({data, isLoading, isFetching}) => ({
        resultingProperties: data?.resultingProperties,
        isLoading: isLoading || isFetching,
      }),
    })

  const [fetchParametersByNameAndValue, parametersByNameAndValue] =
    restApi.endpoints.getBuildProperties.useLazyQuery({
      selectFromResult: ({data, isLoading, isFetching}) => ({
        resultingProperties: data?.resultingProperties,
        isLoading: isLoading || isFetching,
      }),
    })

  const result = useMemo(() => {
    const propertyByName = parametersByName.resultingProperties?.property
    const propertyByValue = parametersByValue.resultingProperties?.property
    const propertyByNameAndValue = parametersByNameAndValue.resultingProperties?.property

    const property = [
      ...(propertyByName ?? []),
      ...(propertyByValue ?? []),
      ...(propertyByNameAndValue ?? []),
    ]

    const count =
      parametersByName.resultingProperties?.count != null &&
      parametersByValue.resultingProperties?.count != null &&
      parametersByNameAndValue.resultingProperties?.count != null
        ? parametersByName.resultingProperties?.count +
          parametersByValue.resultingProperties?.count +
          parametersByNameAndValue.resultingProperties?.count
        : undefined

    return {
      resultingProperties: {
        count,
        property,
      },
      startProperties: Object.fromEntries(
        property.map(value => [value.name, value.value] as [string, string]) ?? [],
      ),
      isLoading:
        parametersByName.isLoading ||
        parametersByValue.isLoading ||
        parametersByNameAndValue.isLoading,
    }
  }, [
    parametersByName.isLoading,
    parametersByName.resultingProperties?.count,
    parametersByName.resultingProperties?.property,
    parametersByNameAndValue.isLoading,
    parametersByNameAndValue.resultingProperties?.count,
    parametersByNameAndValue.resultingProperties?.property,
    parametersByValue.isLoading,
    parametersByValue.resultingProperties?.count,
    parametersByValue.resultingProperties?.property,
  ])

  const fetch = useCallback(
    (props: {buildId: BuildId; normalizedSearch: string; fields: string}) => {
      const {buildId, normalizedSearch, fields} = props

      const locatorByName = `^dep\\..*${normalizedSearch}.*$`
      const controllerByName = fetchParametersByName(
        {
          buildLocator: `id:${buildId}`,
          fields: `resultingProperties(${fields},$locator:(name:(value:(${locatorByName}),matchType:matches,ignoreCase:true),value:${normalizedSearch},matchType:does-not-contain,ignoreCase:true))`,
        },
        true,
      )

      const locatorByValue = `^dep\\..(?!.*${normalizedSearch}).*$`
      const controllerByValue = fetchParametersByValue(
        {
          buildLocator: `id:${buildId}`,
          fields: `resultingProperties(${fields},$locator:(name:(value:(${locatorByValue}),matchType:matches,ignoreCase:true),value:${normalizedSearch},matchType:contains,ignoreCase:true))`,
        },
        true,
      )

      const locatorByNameAndValue = `^dep\\..*${normalizedSearch}.*$`
      const controllerByNameAndValue = fetchParametersByNameAndValue(
        {
          buildLocator: `id:${buildId}`,
          fields: `resultingProperties(${fields},$locator:(name:(value:(${locatorByNameAndValue}),matchType:matches,ignoreCase:true),value:${normalizedSearch},matchType:contains,ignoreCase:true))`,
        },
        true,
      )

      return () => {
        controllerByName.abort()
        controllerByValue.abort()
        controllerByNameAndValue.abort()
      }
    },
    [fetchParametersByName, fetchParametersByNameAndValue, fetchParametersByValue],
  )

  return [fetch, result] as const
}
